/**
 * Pure helpers behind AssistantToolCard (F1). Extracted so the tool→layout
 * mapping and field-extraction logic are unit-testable without mounting Vue.
 */

export type CardKind =
  | "draft_segment"
  | "get_campaign_stats"
  | "compare_periods"
  | "list_recent_broadcasts"
  // D3 — Copilot proposal cards (recommend / draft → human «Применить»).
  | "recommend_offer"
  | "create_journey"
  | "publish_survey"
  // Copilot Apply wave — draft → human opens the prefilled editor (never persists).
  | "draft_broadcast"
  | "create_experience"
  | "explain_decision"
  // Phase-1 READ tools (no write buttons) — list + NBO-by-segment.
  | "list_surveys"
  | "list_clients"
  | "recommend_offer_by_segment"
  | "unknown";

/** Map a tool name to the card layout we render (unknown → JSON fallback). */
export function cardKindForTool(tool: string): CardKind {
  switch (tool) {
    case "draft_segment":
    case "get_campaign_stats":
    case "compare_periods":
    case "list_recent_broadcasts":
    case "recommend_offer":
    case "create_journey":
    case "publish_survey":
    case "draft_broadcast":
    case "create_experience":
    case "explain_decision":
    case "list_surveys":
    case "list_clients":
    case "recommend_offer_by_segment":
      return tool;
    default:
      return "unknown";
  }
}

/** A draft_segment tool output, narrowed to the fields the card renders. */
export interface DraftSegmentView {
  filter: Record<string, unknown> | null;
  audienceSize: number | null;
  confidence: "high" | "medium" | "low";
  explanation: string;
  suggestedName: string;
  unavailable: boolean;
  error: string | null;
}

/** Extract a typed view from a draft_segment tool output. */
export function readDraftSegment(output: unknown): DraftSegmentView {
  const o = asObject(output);
  const error = typeof o.error === "string" ? o.error : null;
  const filter = o.filter && typeof o.filter === "object" && !Array.isArray(o.filter)
    ? (o.filter as Record<string, unknown>)
    : null;
  const explanation = typeof o.explanation === "string" ? o.explanation : "";
  const confRaw = typeof o.confidence === "string" ? o.confidence : "low";
  const confidence = (["high", "medium", "low"].includes(confRaw) ? confRaw : "low") as
    | "high"
    | "medium"
    | "low";
  return {
    filter,
    audienceSize: typeof o.audience_size === "number" ? o.audience_size : null,
    confidence,
    explanation,
    suggestedName: suggestSegmentName(explanation),
    unavailable: o.unavailable === true,
    error,
  };
}

/** Derive a starter segment name from the LLM explanation (first sentence). */
export function suggestSegmentName(explanation: string): string {
  // Coerce defensively — `??` does NOT catch a number/object (that would throw
  // on .split, the exact non-string foot-gun the 1c hardening targets).
  const s = typeof explanation === "string" ? explanation : "";
  const first = s.split(/[.!?\n]/)[0]?.trim() ?? "";
  return (first || "Сегмент из ИИ").slice(0, 80);
}

// ─── D3: recommend_offer (READ — recommendation, no write) ───

/** One recommended offer, narrowed to display fields. */
export interface RecommendedOffer {
  id: string;
  name: string;
  kind: string | null;
  description: string | null;
}

/** A recommend_offer tool output, narrowed to what the card renders. */
export interface RecommendOfferView {
  offer: RecommendedOffer | null;
  /** Fit score 0..1 (rendered as %); null when absent. */
  score: number | null;
  reason: string;
  reasonCodes: string[];
  error: string | null;
}

/**
 * Extract a typed view from a recommend_offer tool output.
 * Contract: { offer: {id,name,kind,description}|null, score, reason, reason_codes[] }.
 * offer===null ⇒ no suitable offer (card shows the empty message).
 */
export function readRecommendOffer(output: unknown): RecommendOfferView {
  const o = asObject(output);
  const error = typeof o.error === "string" ? o.error : null;
  const rawOffer = asObject(o.offer);
  const hasOffer = o.offer != null && typeof o.offer === "object" && typeof rawOffer.id === "string";
  const offer: RecommendedOffer | null = hasOffer
    ? {
        id: String(rawOffer.id),
        name: typeof rawOffer.name === "string" ? rawOffer.name : String(rawOffer.id),
        kind: typeof rawOffer.kind === "string" ? rawOffer.kind : null,
        description: typeof rawOffer.description === "string" ? rawOffer.description : null,
      }
    : null;
  return {
    offer,
    score: typeof o.score === "number" && Number.isFinite(o.score) ? o.score : null,
    reason: typeof o.reason === "string" ? o.reason : "",
    reasonCodes: Array.isArray(o.reason_codes)
      ? (o.reason_codes.filter((c): c is string => typeof c === "string"))
      : [],
    error,
  };
}

// ─── D3: create_journey (PROPOSAL — human «Применить» persists) ───

/** The journey draft payload we forward to the POST /v1/journeys apply call. */
export interface JourneyDraftPayload {
  name: string;
  entry_event_type: string;
  reentry_policy: string;
  flow: unknown[];
}

/** A create_journey tool output, narrowed to what the card renders + the raw draft. */
export interface JourneyDraftView {
  draft: JourneyDraftPayload | null;
  stepCount: number;
  explanation: string;
  /** confidence 0..1 (rendered as %); null when absent. */
  confidence: number | null;
  error: string | null;
}

/**
 * Extract a typed view from a create_journey tool output.
 * Contract: { draft: {name, entry_event_type, reentry_policy, flow[]}, explanation, confidence }.
 */
export function readJourneyDraft(output: unknown): JourneyDraftView {
  const o = asObject(output);
  const error = typeof o.error === "string" ? o.error : null;
  const d = asObject(o.draft);
  const hasDraft = o.draft != null && typeof o.draft === "object" && typeof d.name === "string";
  const flow = Array.isArray(d.flow) ? (d.flow as unknown[]) : [];
  const draft: JourneyDraftPayload | null = hasDraft
    ? {
        name: String(d.name),
        entry_event_type: typeof d.entry_event_type === "string" ? d.entry_event_type : "",
        reentry_policy: typeof d.reentry_policy === "string" ? d.reentry_policy : "once",
        flow,
      }
    : null;
  return {
    draft,
    stepCount: flow.length,
    explanation: typeof o.explanation === "string" ? o.explanation : "",
    confidence: typeof o.confidence === "number" && Number.isFinite(o.confidence) ? o.confidence : null,
    error,
  };
}

// ─── D3: publish_survey (PROPOSAL — human «Применить» creates/publishes) ───

/** The survey draft payload we forward to the survey-create apply call. */
export interface SurveyDraftPayload {
  name: string;
  question: string;
  scale_type: "nps" | "csat" | "thumbs";
  detractor_max?: number;
  promoter_min?: number;
  ask_comment?: boolean;
}

/** A publish_survey tool output, narrowed to what the card renders + the raw draft. */
export interface SurveyDraftView {
  draft: SurveyDraftPayload | null;
  /** Human-readable scale label («NPS 0–10» / «CSAT 1–5» / «👍 / 👎»). */
  scaleLabel: string;
  error: string | null;
}

const SCALE_LABELS: Record<string, string> = {
  nps: "NPS · 0–10",
  csat: "CSAT · 1–5",
  thumbs: "👍 / 👎",
};

/**
 * Extract a typed view from a publish_survey tool output.
 *
 * The engine emits the survey draft object itself (name, question, scale_type,
 * thresholds…); some builds may nest it under `draft`/`survey`. We accept the
 * top-level object first and fall back to a nested wrapper so the card is
 * robust to either shape.
 */
export function readSurveyDraft(output: unknown): SurveyDraftView {
  const top = asObject(output);
  const error = typeof top.error === "string" ? top.error : null;
  // Prefer a nested {draft|survey} wrapper; otherwise treat the output itself as the draft.
  const nested =
    top.draft != null && typeof top.draft === "object"
      ? asObject(top.draft)
      : top.survey != null && typeof top.survey === "object"
        ? asObject(top.survey)
        : top;
  const hasDraft = typeof nested.name === "string" && typeof nested.question === "string";
  const scaleRaw = typeof nested.scale_type === "string" ? nested.scale_type : "nps";
  const scale_type = (["nps", "csat", "thumbs"].includes(scaleRaw) ? scaleRaw : "nps") as
    | "nps"
    | "csat"
    | "thumbs";
  const draft: SurveyDraftPayload | null = hasDraft
    ? {
        name: String(nested.name),
        question: String(nested.question),
        scale_type,
        ...(typeof nested.detractor_max === "number" ? { detractor_max: nested.detractor_max } : {}),
        ...(typeof nested.promoter_min === "number" ? { promoter_min: nested.promoter_min } : {}),
        ...(typeof nested.ask_comment === "boolean" ? { ask_comment: nested.ask_comment } : {}),
      }
    : null;
  return {
    draft,
    scaleLabel: SCALE_LABELS[scale_type] ?? scaleRaw,
    error,
  };
}

// ─── Copilot Apply: draft_broadcast (PROPOSAL — human opens the wizard) ───

/**
 * A draft_broadcast tool output, narrowed to what the card renders + the raw
 * pieces we forward to the broadcast wizard prefill.
 *
 * Engine contract (success):
 *   { name, segment_draft: { filter, audience_size, confidence }, body_text }
 * Engine contract (NL failure):
 *   { error, message, name, body_text }  ← filter could not be built.
 */
export interface DraftBroadcastView {
  /** Proposed broadcast name (used as the wizard ?name= prefill). */
  name: string;
  /** Segment filter to save-then-prefill the wizard; null when NL failed. */
  filter: Record<string, unknown> | null;
  audienceSize: number | null;
  confidence: "high" | "medium" | "low";
  /** Suggested message text — shown for the operator to copy (channel-agnostic). */
  bodyText: string;
  error: string | null;
  /** Human-readable failure message (when error is set). */
  message: string;
}

/** Extract a typed view from a draft_broadcast tool output. */
export function readDraftBroadcast(output: unknown): DraftBroadcastView {
  const o = asObject(output);
  const seg = asObject(o.segment_draft);
  const error = typeof o.error === "string" ? o.error : null;
  const filter =
    seg.filter && typeof seg.filter === "object" && !Array.isArray(seg.filter)
      ? (seg.filter as Record<string, unknown>)
      : null;
  const confRaw = typeof seg.confidence === "string" ? seg.confidence : "low";
  const confidence = (["high", "medium", "low"].includes(confRaw) ? confRaw : "low") as
    | "high"
    | "medium"
    | "low";
  return {
    name: typeof o.name === "string" ? o.name : "",
    filter,
    audienceSize: typeof seg.audience_size === "number" ? seg.audience_size : null,
    confidence,
    bodyText: typeof o.body_text === "string" ? o.body_text : "",
    error,
    message: typeof o.message === "string" ? o.message : "",
  };
}

// ─── Copilot Apply: create_experience (PROPOSAL — human opens the editor) ───

/** A web-tool («впечатление») draft, narrowed to display + the raw draft we forward. */
export interface ExperienceDraftPayload {
  kind: "popup" | "survey";
  title: string;
  content: { text?: string; html?: string };
  trigger: { event_type?: string };
  behavior: { position?: string };
}

/** A create_experience tool output, narrowed to what the card renders + the raw draft. */
export interface ExperienceDraftView {
  draft: ExperienceDraftPayload | null;
  /** Short content snippet (text preferred, else stripped html) for the card. */
  snippet: string;
  error: string | null;
}

/**
 * Extract a typed view from a create_experience tool output.
 * Contract: { draft: { kind, title, content:{text?,html?}, trigger:{event_type?}, behavior:{position?} } }.
 */
export function readExperienceDraft(output: unknown): ExperienceDraftView {
  const o = asObject(output);
  const error = typeof o.error === "string" ? o.error : null;
  const d = asObject(o.draft);
  const hasDraft = o.draft != null && typeof o.draft === "object" && typeof d.title === "string";
  const content = asObject(d.content);
  const trigger = asObject(d.trigger);
  const behavior = asObject(d.behavior);
  const kindRaw = typeof d.kind === "string" ? d.kind : "popup";
  const kind = (kindRaw === "survey" ? "survey" : "popup") as "popup" | "survey";
  const text = typeof content.text === "string" ? content.text : undefined;
  const html = typeof content.html === "string" ? content.html : undefined;
  const draft: ExperienceDraftPayload | null = hasDraft
    ? {
        kind,
        title: String(d.title),
        content: { ...(text !== undefined ? { text } : {}), ...(html !== undefined ? { html } : {}) },
        trigger: typeof trigger.event_type === "string" ? { event_type: trigger.event_type } : {},
        behavior: typeof behavior.position === "string" ? { position: behavior.position } : {},
      }
    : null;
  const snippet = (text ?? (html ? html.replace(/<[^>]*>/g, " ") : "")).replace(/\s+/g, " ").trim().slice(0, 160);
  return { draft, snippet, error };
}

// ─── Copilot Apply: explain_decision (READ — plain-text explanation) ───

/** A explain_decision tool output: a STRING explanation, or {error}. */
export interface ExplainDecisionView {
  /** The human-readable explanation paragraph(s); "" when only an error came. */
  text: string;
  error: string | null;
}

/**
 * Extract a typed view from a explain_decision tool output.
 * The engine returns a STRING (not an object). Some builds may wrap it as
 * { error } on failure — handle both so the card never renders «[object Object]».
 */
export function readExplainDecision(output: unknown): ExplainDecisionView {
  if (typeof output === "string") return { text: output, error: null };
  const o = asObject(output);
  const error = typeof o.error === "string" ? o.error : null;
  // Be lenient: some shapes nest the text under `text`/`explanation`.
  const text =
    typeof o.text === "string" ? o.text : typeof o.explanation === "string" ? o.explanation : "";
  return { text, error };
}

// ─── Phase-1 READ: list_surveys ───

/** One survey row (READ — no write actions). */
export interface SurveyRow {
  id: string;
  name: string;
  /** active | archived | inactive (engine maps archivedAt/active → string). */
  status: string;
  responsesCount: number;
}

/** A list_surveys tool output. Contract: { rows: [{ id, name, status, responses_count, … }] }. */
export interface SurveyListView {
  rows: SurveyRow[];
  error: string | null;
}

/** Extract a typed view from a list_surveys tool output. */
export function readSurveyList(output: unknown): SurveyListView {
  const o = asObject(output);
  const error = typeof o.error === "string" ? o.error : null;
  const rawRows = Array.isArray(o.rows) ? o.rows : [];
  const rows: SurveyRow[] = rawRows
    .map((r) => asObject(r))
    .filter((r) => typeof r.id === "string")
    .map((r) => ({
      id: String(r.id),
      name: typeof r.name === "string" ? r.name : String(r.id),
      status: typeof r.status === "string" ? r.status : "inactive",
      responsesCount:
        typeof r.responses_count === "number" && Number.isFinite(r.responses_count)
          ? r.responses_count
          : 0,
    }));
  return { rows, error };
}

// ─── Phase-1 READ: list_clients (FULL PII shown in the UI — intended) ───

/**
 * One client row. Per SPEC §2.2 the UI card renders FULL email/phone (PII is
 * shown to the operator; only the LLM-bound copy is redacted on the engine).
 */
export interface ClientRow {
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  lifecycleStage: string | null;
  region: string | null;
}

/** A list_clients tool output. Contract: { rows: [{ id, display_name, email, phone, lifecycle_stage, region }] }. */
export interface ClientListView {
  rows: ClientRow[];
  error: string | null;
}

/** Extract a typed view from a list_clients tool output. */
export function readClientList(output: unknown): ClientListView {
  const o = asObject(output);
  const error = typeof o.error === "string" ? o.error : null;
  const rawRows = Array.isArray(o.rows) ? o.rows : [];
  const rows: ClientRow[] = rawRows
    .map((r) => asObject(r))
    .filter((r) => typeof r.id === "string")
    .map((r) => ({
      id: String(r.id),
      displayName: typeof r.display_name === "string" ? r.display_name : "",
      email: typeof r.email === "string" ? r.email : null,
      phone: typeof r.phone === "string" ? r.phone : null,
      lifecycleStage: typeof r.lifecycle_stage === "string" ? r.lifecycle_stage : null,
      region: typeof r.region === "string" ? r.region : null,
    }));
  return { rows, error };
}

// ─── Phase-1 READ: recommend_offer_by_segment (NBO over a segment sample) ───

/** One aggregated offer recommendation for the segment. */
export interface SegmentOfferRow {
  offerId: string;
  offerName: string;
  /** How many sampled members had this as their best offer. */
  count: number;
  /** count / sampled, 0..1 (rendered as %); null when absent. */
  shareOfSegment: number | null;
  /** Average decide() score 0..1 across the members; null when absent. */
  avgScore: number | null;
  rationale: string;
}

/**
 * A recommend_offer_by_segment tool output.
 * Contract: { sampled, source:{segment_id?|nl_query?}, offers:[{ offer_id, offer_name, count, share_of_segment, avg_score, rationale }] }.
 */
export interface SegmentOffersView {
  /** Number of members sampled from the segment (cap 200 on the engine). */
  sampled: number | null;
  rows: SegmentOfferRow[];
  error: string | null;
}

/** Extract a typed view from a recommend_offer_by_segment tool output. */
export function readSegmentOffers(output: unknown): SegmentOffersView {
  const o = asObject(output);
  const error = typeof o.error === "string" ? o.error : null;
  const rawRows = Array.isArray(o.offers) ? o.offers : [];
  const rows: SegmentOfferRow[] = rawRows
    .map((r) => asObject(r))
    .filter((r) => typeof r.offer_id === "string")
    .map((r) => ({
      offerId: String(r.offer_id),
      offerName: typeof r.offer_name === "string" ? r.offer_name : String(r.offer_id),
      count: typeof r.count === "number" && Number.isFinite(r.count) ? r.count : 0,
      shareOfSegment:
        typeof r.share_of_segment === "number" && Number.isFinite(r.share_of_segment)
          ? r.share_of_segment
          : null,
      avgScore:
        typeof r.avg_score === "number" && Number.isFinite(r.avg_score) ? r.avg_score : null,
      rationale: typeof r.rationale === "string" ? r.rationale : "",
    }));
  return {
    sampled: typeof o.sampled === "number" && Number.isFinite(o.sampled) ? o.sampled : null,
    rows,
    error,
  };
}

function asObject(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}
